import type { ReactNode } from "react";
import {
  resolveForfeoUxFeedbackMeta,
  type ForfeoUxFeedbackState,
} from "../../lib/forfeoUxFeedbackContract";

const stateStyles: Record<ForfeoUxFeedbackState, string> = {
  loading: "border-slate-200 bg-slate-50 text-slate-700",
  empty: "border-slate-200 bg-white text-slate-700",
  error: "border-red-200 bg-red-50 text-red-900",
  offline: "border-amber-200 bg-amber-50 text-amber-950",
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
};

const stateGlyphs: Record<Exclude<ForfeoUxFeedbackState, "loading">, string> = {
  empty: "i",
  error: "!",
  offline: "↻",
  success: "✓",
};

export type ForfeoFeedbackStateProps = {
  state: ForfeoUxFeedbackState;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  compact?: boolean;
  className?: string;
};

export function ForfeoFeedbackState({
  state,
  title,
  description,
  actionLabel,
  onAction,
  compact = false,
  className = "",
}: ForfeoFeedbackStateProps) {
  const meta = resolveForfeoUxFeedbackMeta(state);

  return (
    <section
      role={meta.role}
      aria-live={meta.live}
      aria-busy={meta.busy || undefined}
      data-forfeo-ux-state={state}
      className={`w-full rounded-2xl border ${stateStyles[state]} ${
        compact ? "p-4" : "px-5 py-8 sm:px-8 sm:py-10"
      } ${className}`.trim()}
    >
      <div className="mx-auto flex max-w-xl flex-col items-center text-center">
        {state === "loading" ? (
          <span
            aria-hidden="true"
            className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-current border-r-transparent motion-reduce:animate-none"
          />
        ) : (
          <span
            aria-hidden="true"
            className="mb-4 grid h-10 w-10 place-items-center rounded-full border border-current text-lg font-black"
          >
            {stateGlyphs[state]}
          </span>
        )}
        <h2 className="text-base font-black tracking-tight sm:text-lg">{title}</h2>
        {description ? (
          <p className="mt-2 max-w-prose text-sm leading-6 opacity-80 sm:text-base">
            {description}
          </p>
        ) : null}
        {actionLabel && onAction ? (
          <button
            type="button"
            onClick={onAction}
            className="mt-5 inline-flex min-h-11 items-center justify-center rounded-xl border border-current px-5 py-2.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </section>
  );
}

export type ForfeoAsyncBoundaryProps = {
  loading: boolean;
  error?: Error | string | null;
  empty?: boolean;
  children: ReactNode;
  loadingTitle?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  errorTitle?: string;
  errorDescription?: string;
  retryLabel?: string;
  onRetry?: () => void;
};

export function ForfeoAsyncBoundary({
  loading,
  error,
  empty = false,
  children,
  loadingTitle = "Chargement en cours",
  emptyTitle = "Aucun résultat pour le moment",
  emptyDescription = "Revenez plus tard ou modifiez vos critères.",
  errorTitle = "Impossible d’afficher cette section",
  errorDescription = "Une erreur temporaire est survenue. Réessayez dans quelques instants.",
  retryLabel = "Réessayer",
  onRetry,
}: ForfeoAsyncBoundaryProps) {
  if (loading) {
    return <ForfeoFeedbackState state="loading" title={loadingTitle} />;
  }
  if (error) {
    return (
      <ForfeoFeedbackState
        state="error"
        title={errorTitle}
        description={errorDescription}
        actionLabel={onRetry ? retryLabel : undefined}
        onAction={onRetry}
      />
    );
  }
  if (empty) {
    return (
      <ForfeoFeedbackState
        state="empty"
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }
  return <div data-forfeo-ux-boundary="ready">{children}</div>;
}
